import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment-timezone";
import { MbscEventDragEvent, MbscEventUpdateEvent } from "@mobiscroll/react";
import { snackbarService } from "@one/web-components/dist/index.cjs.js";
import { COLORS } from "../constants";
import { EventData } from "../types";

interface DragOverlay {
  el: HTMLElement;
  alignedBaseWidth: number;
}

const createDragOverlays = (
  otherIds: string[],
  gridEl: HTMLElement,
  backgroundColor: string,
  draggedRightInGrid: number,
): DragOverlay[] => {
  const gridRect = gridEl.getBoundingClientRect();
  const overlays: DragOverlay[] = [];

  otherIds.forEach((otherId) => {
    const otherEl = document.querySelector<HTMLElement>(`.mbsc-schedule-event[data-id="${otherId}"]`);
    if (!otherEl) { return; }

    const otherRect = otherEl.getBoundingClientRect();
    const left = otherRect.left - gridRect.left + gridEl.scrollLeft;
    const top = otherRect.top - gridRect.top + gridEl.scrollTop;

    const overlay = document.createElement("div");
    overlay.className = "change-end-time-modal__drag-overlay";
    overlay.style.left = `${left}px`;
    overlay.style.top = `${top}px`;
    overlay.style.width = `${otherRect.width}px`;
    overlay.style.height = `${otherRect.height}px`;
    overlay.style.background = backgroundColor;
    gridEl.appendChild(overlay);
    overlays.push({ el: overlay, alignedBaseWidth: draggedRightInGrid - left });
  });

  return overlays;
};

interface UseEventResizingParams {
  otherBookings: EventData[];
  isSelected: (id: string) => boolean;
  currentBookingEvents: EventData[];
  minAllowedEndTime: moment.Moment;
  isOpen: boolean;
}

const useEventResizing = ({
  otherBookings,
  isSelected,
  currentBookingEvents,
  minAllowedEndTime,
  isOpen,
}: UseEventResizingParams) => {
  const dragCleanupRef = useRef<(() => void) | null>(null);
  const otherEventIdsRef = useRef<string[]>([]);
  const [resizedEntries, setResizedEntries] = useState<Map<string, { start: string; end: string }>>(new Map());

  useEffect(() => {
    if (!isOpen) {
      setResizedEntries(new Map());
    }
  }, [isOpen]);

  const adjustedCurrentBookingEvents = useMemo(() => {
    return currentBookingEvents.map((event) => {
      const resizedEntry = resizedEntries.get(String(event.id));
      return { ...event, ...(resizedEntry && { end: resizedEntry.end }) };
    });
  }, [currentBookingEvents, resizedEntries]);

  const hasChanges = useMemo(() => {
    if (resizedEntries.size === 0) {
      return false;
    }
    return Array.from(resizedEntries.entries()).some(([id, entry]) => {
      const original = currentBookingEvents.find((e) => String(e.id) === id);
      if (!original) return true;
      return moment(entry.end).valueOf() !== moment(original.end).valueOf();
    });
  }, [resizedEntries, currentBookingEvents]);

  const checkOverlapForResource = useCallback(
    (resourceId: string, eventId: string, newStart: Date | string, newEnd: Date | string): boolean => {
      const resourceOtherBookings = otherBookings.filter((e) => String(e.resource) === resourceId && String(e.id) !== eventId);
      const newStartTime = moment(newStart).valueOf();
      const newEndTime = moment(newEnd).valueOf();
      return resourceOtherBookings.some((otherEvent) => {
        const otherStart = moment(otherEvent.start).valueOf();
        const otherEnd = moment(otherEvent.end).valueOf();
        return newStartTime < otherEnd && newEndTime > otherStart;
      });
    },
    [otherBookings],
  );

  const getSelectedSlotCohort = useCallback(
    (targetEvent: EventData): EventData[] => {
      const targetStart = moment(targetEvent.start).valueOf();
      const cohort = currentBookingEvents.filter(
        (eventItem) =>
          eventItem.isCurrentBooking &&
          isSelected(String(eventItem.resource)) &&
          moment(eventItem.start).valueOf() === targetStart,
      );

      if (cohort.length > 0) {
        return cohort;
      }

      return currentBookingEvents.filter((eventItem) => String(eventItem.id) === String(targetEvent.id));
    },
    [currentBookingEvents, isSelected],
  );

  const handleEventDragStart = useCallback(
    (args: MbscEventDragEvent) => {
      const event = args.event as EventData;
      if (!isSelected(String(event.resource)) || !event.isCurrentBooking) { return; }

      const linkedSlotEvents = getSelectedSlotCohort(event);
      const otherIds = linkedSlotEvents
        .filter((e) => String(e.id) !== String(event.id))
        .map((e) => String(e.id));

      if (otherIds.length === 0) { return; }

      otherEventIdsRef.current = otherIds;

      const startClientX: number = args.domEvent?.clientX ?? 0;
      const draggedEl = document.querySelector<HTMLElement>(`.mbsc-schedule-event[data-id="${String(event.id)}"]`);
      const draggedRect = draggedEl?.getBoundingClientRect();
      const eventDurationMs = moment(event.end as string | Date).diff(moment(event.start as string | Date));
      const pixelsPerMs = draggedRect && eventDurationMs > 0 ? draggedRect.width / eventDurationMs : 0;
      const snapPx = pixelsPerMs * 30 * 60 * 1000;

      const gridEl = document.querySelector<HTMLElement>(".mbsc-timeline-grid");
      if (!gridEl) { return; }

      const gridRect = gridEl.getBoundingClientRect();
      const draggedRightInGrid = draggedRect ? (draggedRect.right - gridRect.left + gridEl.scrollLeft) : 0;
      const overlays = createDragOverlays(otherIds, gridEl, COLORS.currentBooking.background, draggedRightInGrid);

      const onPointerMove = (e: PointerEvent) => {
        const rawDelta = e.clientX - startClientX;
        const deltaX = snapPx > 0 ? Math.round(rawDelta / snapPx) * snapPx : rawDelta;
        overlays.forEach(({ el, alignedBaseWidth }) => {
          el.style.width = `${Math.max(4, alignedBaseWidth + deltaX)}px`;
        });
        otherIds.forEach((otherId) => {
          const el = document.querySelector<HTMLElement>(`.mbsc-schedule-event[data-id="${otherId}"]`);
          if (el) { el.style.opacity = "0.5"; }
        });
      };

      document.addEventListener("pointermove", onPointerMove);

      dragCleanupRef.current = () => {
        document.removeEventListener("pointermove", onPointerMove);
        overlays.forEach(({ el }) => el.remove());
        otherIds.forEach((otherId) => {
          const el = document.querySelector<HTMLElement>(`.mbsc-schedule-event[data-id="${otherId}"]`);
          if (el) { el.style.opacity = ""; }
        });
      };
    },
    [isSelected, getSelectedSlotCohort],
  );

  const handleEventDragEnd = useCallback(() => {
    if (dragCleanupRef.current) {
      dragCleanupRef.current();
      dragCleanupRef.current = null;
    }
    otherEventIdsRef.current = [];
  }, []);

  const handleEventUpdate = useCallback(
    (args: MbscEventUpdateEvent) => {
      const { event, oldEvent } = args;

      if (!event.isCurrentBooking) { return false; }
      if (!oldEvent) { return false; }

      const oldStartTime = moment(oldEvent.start).valueOf();
      const newStartTime = moment(event.start).valueOf();
      if (oldStartTime !== newStartTime) { return false; }

      const newEndTime = moment(event.end).valueOf();
      if (newEndTime <= oldStartTime) { return false; }
      if (moment(event.end) < minAllowedEndTime) { return false; }

      const draggedResourceId = String(event.resource);
      const isDraggedEventSelected = isSelected(draggedResourceId);

      if (!isDraggedEventSelected) {
        const hasOverlap = checkOverlapForResource(draggedResourceId, String(event.id), event.start as string | Date, event.end as string | Date);
        if (hasOverlap) {
          snackbarService.show({
            message: "Cannot change end time — conflicts with an existing booking on this equipment",
            type: "warning",
            duration: 4000,
          });
          return false;
        }
        setResizedEntries((prev) => {
          const newMap = new Map(prev);
          newMap.set(String(event.id), {
            start: moment(event.start).utc().format(),
            end: moment(event.end).utc().format(),
          });
          return newMap;
        });
        return true;
      }

      const selectedCurrentBookingEvents = getSelectedSlotCohort(event as EventData);

      const newEndMoment = moment(event.end);
      const hasTooShortDuration = selectedCurrentBookingEvents.some((selectedEvent) => {
        const eventStart = moment(selectedEvent.start);
        return newEndMoment.diff(eventStart, "minutes") < 30;
      });

      if (hasTooShortDuration) {
        snackbarService.show({
          message: "Cannot change end time — booking duration must be at least 30 minutes for all selected equipment",
          type: "warning",
          duration: 4000,
        });
        return false;
      }

      const hasAnyOverlap = selectedCurrentBookingEvents.some((selectedEvent) =>
        checkOverlapForResource(
          String(selectedEvent.resource),
          String(selectedEvent.id),
          selectedEvent.start as string | Date,
          event.end as string | Date,
        ),
      );

      if (hasAnyOverlap) {
        snackbarService.show({
          message: "Cannot change end time — conflicts with existing bookings on selected equipment",
          type: "warning",
          duration: 4000,
        });
        return false;
      }

      setResizedEntries((prev) => {
        const newMap = new Map(prev);
        selectedCurrentBookingEvents.forEach((selectedEvent) => {
          newMap.set(String(selectedEvent.id), {
            start: moment(selectedEvent.start).utc().format(),
            end: moment(event.end).utc().format(),
          });
        });
        return newMap;
      });

      return true;
    },
    [isSelected, checkOverlapForResource, minAllowedEndTime, getSelectedSlotCohort],
  );

  return { resizedEntries, hasChanges, adjustedCurrentBookingEvents, handleEventDragStart, handleEventDragEnd, handleEventUpdate };
};

export default useEventResizing;
